/**
 * シーンのつなぎ (トランジション) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-H。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/transition.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/transition.py (AI 提案の正規化・要約。描画はしない)
 *
 * ## 種類
 * `cut` (重なりなし) と、前のシーンと `durationSec` だけ重なる 6 種:
 * `crossfade` / `wipe` / `slide` / `zoom` / `whiteout` / `flash`。
 *
 * ## 時間モデル (重要)
 * **どの種類も crossfade と完全に同じ**: 「このシーンの先頭が、直前のシーンの末尾と
 * `durationSec` だけ重なる」。総尺・各シーンの開始秒・テロップ / ナレーション / BGM の絶対秒は
 * すべて既存の crossfade の式 (transitionOverlapSec を差し引く) をそのまま使い、
 * 種類ごとの時間の式は作らない。先頭シーンのトランジションは無視される (重なる相手がいない)。
 *
 * ## 描画
 * 重なり区間の進行 p (0〜1) に対して evaluateTransition が「入ってくるシーン層」と
 * 「出ていくシーン層」に掛ける値を返す。レイヤーの重なりは **出ていく側が下・入ってくる側が上**
 * (Remotion の Sequence が DOM 順に重なる) で、crossfade は上の層の opacity だけで表現している。
 * 出ていく側の opacity は常に 1 のまま (下げると背後の黒が透けて暗くなる) で、
 * これは crossfade からの一貫した扱い。
 */
import { z } from 'zod';
import { easeMotion } from './motion';
import { formatDurationLabel } from './timecode';

export const TRANSITION_TYPES = [
  'cut',
  'crossfade',
  'wipe',
  'slide',
  'zoom',
  'whiteout',
  'flash',
] as const;
export type TransitionType = (typeof TRANSITION_TYPES)[number];

/** 方向を持つ種類 (それ以外の種類では direction を正規化で落とす)。 */
export const TRANSITION_DIRECTIONAL_TYPES: readonly TransitionType[] = ['wipe', 'slide'];

export const TRANSITION_DIRECTIONS = ['left', 'right', 'up', 'down'] as const;
export type TransitionDirection = (typeof TRANSITION_DIRECTIONS)[number];

export const DEFAULT_TRANSITION_DIRECTION: TransitionDirection = 'left';

/**
 * durationSec の範囲。crossfade の既存の範囲 (edit_ops の _op_set_item_transition が
 * 0.1〜3.0 でクランプしていた値) をそのまま他の種類にも使い、`flash` (一瞬の閃光) だけ
 * 上限を短くする。cut は常に 0。
 */
export const TRANSITION_DURATION_MIN_SEC = 0.1;
export const TRANSITION_DURATION_MAX_SEC = 3.0;
export const FLASH_DURATION_MIN_SEC = 0.1;
export const FLASH_DURATION_MAX_SEC = 0.5;

/** 種類ごとの既定の長さ (AI / UI が durationSec を省略したときの解釈)。 */
export const TRANSITION_DEFAULT_DURATION_SEC: Record<TransitionType, number> = {
  cut: 0,
  crossfade: 0.4,
  wipe: 0.4,
  slide: 0.4,
  zoom: 0.4,
  whiteout: 0.4,
  flash: 0.25,
};

/** その種類で許される durationSec の範囲 (cut は 0 固定)。 */
export function transitionDurationRange(type: TransitionType): { min: number; max: number } {
  if (type === 'cut') return { min: 0, max: 0 };
  if (type === 'flash') return { min: FLASH_DURATION_MIN_SEC, max: FLASH_DURATION_MAX_SEC };
  return { min: TRANSITION_DURATION_MIN_SEC, max: TRANSITION_DURATION_MAX_SEC };
}

export const TransitionTypeSchema = z.enum(TRANSITION_TYPES);
export const TransitionDirectionSchema = z.enum(TRANSITION_DIRECTIONS);

export const TransitionSchema = z.object({
  type: TransitionTypeSchema,
  durationSec: z.number().nonnegative(),
  /** wipe / slide のときだけ意味を持つ (他の種類では正規化で落とす)。省略 = left。 */
  direction: TransitionDirectionSchema.optional(),
});
export type Transition = z.infer<typeof TransitionSchema>;

/** UI / AI への説明で使う表示名。 */
export const TRANSITION_LABELS: Record<TransitionType, string> = {
  cut: 'カット',
  crossfade: 'クロスフェード',
  wipe: 'ワイプ',
  slide: 'スライド',
  zoom: 'ズーム',
  whiteout: 'ホワイトアウト',
  flash: 'フラッシュ',
};

/**
 * 方向の表示名は種類で意味が変わるので分けて持つ (下の evaluateTransition の規則どおり):
 * - wipe: direction = **開き始める辺** (left = 左端から右へ開く)
 * - slide: direction = **滑る向き** (left = 右端から入って左へ動く)
 */
export const WIPE_DIRECTION_LABELS: Record<TransitionDirection, string> = {
  left: '左から',
  right: '右から',
  up: '上から',
  down: '下から',
};
export const SLIDE_DIRECTION_LABELS: Record<TransitionDirection, string> = {
  left: '左へ',
  right: '右へ',
  up: '上へ',
  down: '下へ',
};

export function transitionDirectionLabel(type: TransitionType, direction: TransitionDirection): string {
  return type === 'slide' ? SLIDE_DIRECTION_LABELS[direction] : WIPE_DIRECTION_LABELS[direction];
}

/** その種類が方向を持つか。 */
export function transitionHasDirection(type: TransitionType): boolean {
  return TRANSITION_DIRECTIONAL_TYPES.includes(type);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function isTransitionType(value: unknown): value is TransitionType {
  return typeof value === 'string' && (TRANSITION_TYPES as readonly string[]).includes(value);
}

function isTransitionDirection(value: unknown): value is TransitionDirection {
  return typeof value === 'string' && (TRANSITION_DIRECTIONS as readonly string[]).includes(value);
}

/**
 * 外部入力 (AI の提案・古い保存データ・UI) の transition を保存形へ正規化する。
 * 文字列 ("crossfade") とオブジェクト ({type, durationSec, direction}) のどちらも受ける。
 * **保存値 = 実効値**: 範囲外の durationSec はクランプ (種類は維持する)、方向を持たない種類の
 * direction は落とし、方向を持つ種類には既定 (left) を埋める。
 * 不明な種類・壊れた入力は cut に倒す (描画で無視される安全側)。
 */
export function normalizeTransition(raw: unknown): Transition {
  const source =
    typeof raw === 'string'
      ? { type: raw as unknown, durationSec: undefined as unknown, direction: undefined as unknown }
      : raw && typeof raw === 'object'
        ? (raw as { type?: unknown; durationSec?: unknown; direction?: unknown })
        : {};
  const type: TransitionType = isTransitionType(source.type) ? source.type : 'cut';
  if (type === 'cut') return { type: 'cut', durationSec: 0 };

  const range = transitionDurationRange(type);
  const rawDuration = source.durationSec;
  const durationSec =
    typeof rawDuration === 'number' && Number.isFinite(rawDuration)
      ? clamp(rawDuration, range.min, range.max)
      : TRANSITION_DEFAULT_DURATION_SEC[type];

  const next: Transition = { type, durationSec: round4(durationSec) };
  if (transitionHasDirection(type)) {
    next.direction = isTransitionDirection(source.direction) ? source.direction : DEFAULT_TRANSITION_DIRECTION;
  }
  return next;
}

/**
 * このシーンが直前のシーンと重なる秒数。**種類に関わらず** cut 以外は durationSec ぶん重なる
 * (時間モデルは crossfade と同一)。先頭シーンでは呼び出し側が 0 として扱う。
 */
export function transitionOverlapSec(transition: Transition | undefined | null): number {
  // 保存形に無い種類 (旧データ・外部から直接書き換えられた JSON) は cut と同じ扱いにする
  // (backend の transition_overlap_sec と同じ規則。ここで弾かないと総尺だけがずれる)。
  if (!transition || !isTransitionType(transition.type) || transition.type === 'cut') return 0;
  const duration = transition.durationSec;
  return typeof duration === 'number' && Number.isFinite(duration) && duration > 0 ? duration : 0;
}

/**
 * 要約・UI の表示用 (「クロスフェード 0.4秒」「ワイプ 左から 0.5秒」)。
 * 保存形に無い種類 (旧データ・外部から直接書き換えられた JSON) は transitionOverlapSec と
 * 同じく cut として扱う (重ならないものを「ワイプ」と説明しない)。
 * `fps` を渡すと長さを画面向けに書く ("S:FF" = 秒:フレーム: 「クロスフェード 0:10」)。
 * 省略時は秒のまま (AI の要約・backend の文言と揃える)。
 */
export function describeTransition(transition: Transition | undefined | null, fps?: number): string {
  if (!transition || !isTransitionType(transition.type) || transition.type === 'cut') {
    return TRANSITION_LABELS.cut;
  }
  const label = TRANSITION_LABELS[transition.type];
  const direction = transitionHasDirection(transition.type)
    ? ` ${transitionDirectionLabel(transition.type, transition.direction ?? DEFAULT_TRANSITION_DIRECTION)}`
    : '';
  const length = fps === undefined
    ? `${round4(transition.durationSec)}秒`
    : formatDurationLabel(transition.durationSec, fps);
  return `${label}${direction} ${length}`;
}

/**
 * 重なり区間の 1 時点で、前後 2 つのシーン層に掛ける値。
 * 入ってくる側 (incoming) が上・出ていく側 (outgoing) が下に重なっている前提。
 * undefined のキーはその層に何も足さない (既存プロジェクトの DOM を変えないため)。
 */
export interface TransitionRenderState {
  /** 入ってくるシーン層の不透明度 (0〜1)。 */
  incomingOpacity: number;
  /** 入ってくるシーン層に掛ける CSS transform。 */
  incomingTransform?: string;
  /** 入ってくるシーン層に掛ける CSS clip-path。 */
  incomingClipPath?: string;
  /** 入ってくるシーン層の上に敷く白の不透明度 (0 = 敷かない)。 */
  incomingWhiteOpacity: number;
  /** 出ていくシーン層に掛ける CSS transform。 */
  outgoingTransform?: string;
  /** 出ていくシーン層の上に敷く白の不透明度 (0 = 敷かない)。 */
  outgoingWhiteOpacity: number;
}

/** 何も起きていない状態 (cut・重なり 0・範囲外の進行)。 */
export const TRANSITION_IDENTITY: TransitionRenderState = {
  incomingOpacity: 1,
  incomingWhiteOpacity: 0,
  outgoingWhiteOpacity: 0,
};

/** zoom (クロスズーム) の拡大率。出ていく側は 1 → これ、入ってくる側はこれ → 1。 */
export const ZOOM_TRANSITION_SCALE = 1.15;
/** slide の移動量 (画面の 100%)。 */
const SLIDE_TRAVEL_PERCENT = 100;

/**
 * 重なり区間の進行 p (0〜1) における描画パラメータ。
 *
 * - crossfade: 上の層の不透明度が 0 → 1 (従来どおり)
 * - wipe: 上の層を direction の辺から clip-path で開く (不透明度は 1 のまま)
 * - slide: 上の層が direction の逆側から滑り込む (ease-in-out。cover であって push ではない)
 * - zoom: 出ていく側が 1.0 → 1.15、入ってくる側が 1.15 → 1.0 に縮みながら現れる (クロスズーム)
 * - whiteout / flash: 白が p=0.5 でピーク 1 の三角波。p<0.5 は前のシーン、p>=0.5 は次のシーンを
 *   そのまま出す (白が最大の瞬間に切り替えるので、切り替わりは見えない)。
 *   whiteout は ease-in-out で滑らかに飛ばし、flash は linear で一瞬光らせる
 */
export function evaluateTransition(
  type: TransitionType,
  direction: TransitionDirection | undefined,
  p: number,
): TransitionRenderState {
  const t = clamp(p, 0, 1);
  const dir = direction ?? DEFAULT_TRANSITION_DIRECTION;
  switch (type) {
    case 'crossfade':
      return { ...TRANSITION_IDENTITY, incomingOpacity: round4(t) };
    case 'wipe': {
      // 残す (隠す) 割合。left なら右側の inset が 100% → 0% に縮み、左端から右へ開く。
      const hidden = round4((1 - t) * 100);
      const clipPath =
        dir === 'left'
          ? `inset(0% ${hidden}% 0% 0%)`
          : dir === 'right'
            ? `inset(0% 0% 0% ${hidden}%)`
            : dir === 'up'
              ? `inset(0% 0% ${hidden}% 0%)`
              : `inset(${hidden}% 0% 0% 0%)`;
      return { ...TRANSITION_IDENTITY, incomingClipPath: clipPath };
    }
    case 'slide': {
      const e = easeMotion('ease-in-out', t);
      const travel = round4((1 - e) * SLIDE_TRAVEL_PERCENT);
      // direction は「滑る向き」。left = 右端から入って左へ動く。
      const transform =
        dir === 'left'
          ? `translate(${travel}%, 0%)`
          : dir === 'right'
            ? `translate(${-travel}%, 0%)`
            : dir === 'up'
              ? `translate(0%, ${travel}%)`
              : `translate(0%, ${-travel}%)`;
      return { ...TRANSITION_IDENTITY, incomingTransform: transform };
    }
    case 'zoom': {
      const e = easeMotion('ease-in-out', t);
      const incomingScale = round4(ZOOM_TRANSITION_SCALE + (1 - ZOOM_TRANSITION_SCALE) * e);
      const outgoingScale = round4(1 + (ZOOM_TRANSITION_SCALE - 1) * e);
      return {
        incomingOpacity: round4(t),
        incomingTransform: `scale(${incomingScale})`,
        incomingWhiteOpacity: 0,
        outgoingTransform: `scale(${outgoingScale})`,
        outgoingWhiteOpacity: 0,
      };
    }
    case 'whiteout':
    case 'flash': {
      // 0 → 1 → 0 の三角波。whiteout は 2 次のイージング、flash は等速。
      const half = t < 0.5 ? t * 2 : (1 - t) * 2;
      const white = round4(type === 'whiteout' ? easeMotion('ease-in-out', half) : half);
      return {
        // 白が最大 (p=0.5) の瞬間に入れ替える。中間で混ぜると白の下で二重像になる
        incomingOpacity: t < 0.5 ? 0 : 1,
        incomingWhiteOpacity: t < 0.5 ? 0 : white,
        outgoingWhiteOpacity: t < 0.5 ? white : 0,
      };
    }
    case 'cut':
    default:
      return { ...TRANSITION_IDENTITY };
  }
}
